export interface NativeUsageSnapshot {
  model: string | null
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheCreationTokens: number
  contextWindowTokens: number | null
  updatedAt: string
}

export interface NativeUsageDerived {
  contextTokens: number
  contextPercent: number | null
  cachePercent: number | null
}

const LS_NATIVE_USAGE = 'kohaku.nativeUsage.latest'

function nonNegativeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

export function buildNativeUsageSnapshot(
  usage: Record<string, unknown> | undefined,
  model: string | undefined,
  contextWindowTokens: number | null,
): NativeUsageSnapshot | null {
  if (!usage) return null
  let contextUsage: Record<string, unknown> | null = usage
  if (Array.isArray(usage.iterations) && usage.iterations.length > 0) {
    const current = usage.iterations[usage.iterations.length - 1]
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      contextUsage = current as Record<string, unknown>
    }
  }
  const inputTokens = nonNegativeNumber(contextUsage?.input_tokens)
  const outputTokens = nonNegativeNumber(usage.output_tokens)
  const cacheReadTokens = nonNegativeNumber(contextUsage?.cache_read_input_tokens)
  const cacheCreationTokens = nonNegativeNumber(contextUsage?.cache_creation_input_tokens)
  if (inputTokens === null && outputTokens === null && cacheReadTokens === null && cacheCreationTokens === null) return null
  return {
    model: model || null,
    inputTokens: inputTokens ?? 0,
    outputTokens: outputTokens ?? 0,
    cacheReadTokens: cacheReadTokens ?? 0,
    cacheCreationTokens: cacheCreationTokens ?? 0,
    contextWindowTokens,
    updatedAt: new Date().toISOString(),
  }
}

export function deriveNativeUsage(snapshot: NativeUsageSnapshot): NativeUsageDerived {
  const contextTokens = snapshot.inputTokens + snapshot.cacheReadTokens + snapshot.cacheCreationTokens
  return {
    contextTokens,
    contextPercent: snapshot.contextWindowTokens && snapshot.contextWindowTokens > 0
      ? (contextTokens / snapshot.contextWindowTokens) * 100
      : null,
    cachePercent: contextTokens > 0 ? (snapshot.cacheReadTokens / contextTokens) * 100 : null,
  }
}

export function formatTokenCount(value: number): string {
  if (value >= 1_000_000) {
    const scaled = value / 1_000_000
    return `${scaled >= 10 || Number.isInteger(scaled) ? scaled.toFixed(0) : scaled.toFixed(1)}M`
  }
  if (value >= 1_000) {
    const scaled = value / 1_000
    return `${scaled >= 100 || Number.isInteger(scaled) ? scaled.toFixed(0) : scaled.toFixed(1)}k`
  }
  return Math.round(value).toLocaleString()
}

export function formatNativeModel(model: string | null): string {
  if (!model) return 'Native'
  const match = model.match(/^claude-(opus|sonnet|haiku)-(\d+)-(\d+)$/i)
  if (!match) return model
  const family = match[1][0].toUpperCase() + match[1].slice(1).toLowerCase()
  return `${family} ${match[2]}.${match[3]}`
}

function isSnapshot(value: unknown): value is NativeUsageSnapshot {
  if (!value || typeof value !== 'object') return false
  const item = value as Partial<NativeUsageSnapshot>
  return (
    (item.model === null || typeof item.model === 'string') &&
    typeof item.inputTokens === 'number' &&
    typeof item.outputTokens === 'number' &&
    typeof item.cacheReadTokens === 'number' &&
    typeof item.cacheCreationTokens === 'number' &&
    (item.contextWindowTokens === null || typeof item.contextWindowTokens === 'number') &&
    typeof item.updatedAt === 'string'
  )
}

export function readNativeUsageSnapshot(): NativeUsageSnapshot | null {
  try {
    const raw = localStorage.getItem(LS_NATIVE_USAGE)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    return isSnapshot(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function writeNativeUsageSnapshot(snapshot: NativeUsageSnapshot): void {
  try {
    localStorage.setItem(LS_NATIVE_USAGE, JSON.stringify(snapshot))
  } catch {}
}
